<script setup lang="ts">
/**
 * The strip and its panes, built from `tabs`. A pane's content is the slot **named after
 * that tab's `name`** — there are no child components to write.
 *
 * Element Plus builds every pane up front (`lazy` is off and cannot be reached from here),
 * so a tab whose body is a whole widget — its own queries, its own table — goes **outside**
 * this component: pass no slots at all, use it as the strip alone, and switch the bodies
 * below it on the model. Otherwise every tab's widget mounts and fetches on arrival.
 *
 * A form split across tabs drives the model with `useErrorSection`, so a rejected field's
 * tab comes forward.
 */
defineProps<{
  modelValue: string
  tabs: { label: string; name: string }[]
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()
</script>

<template>
  <el-tabs
    :model-value="modelValue"
    v-bind="$attrs"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <el-tab-pane
      v-for="tab in tabs"
      :key="tab.name"
      :label="tab.label"
      :name="tab.name"
    >
      <slot :name="tab.name" />
    </el-tab-pane>
  </el-tabs>
</template>
